import { BadgeChip, useKudosBadges } from './badges';
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { ImageGallery } from '@/components/media/ImageGallery';
import { ReactionBar } from '@/components/reactions/ReactionBar';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Award, Cake, Plus, ImagePlus, MessageCircle, Search, Target, Trash2, UserPlus, X } from 'lucide-react';
import { uploadFailureText, type KudosPost } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { useDebounced } from '@/lib/useDebounced';
import { useAuth } from '@/lib/auth';
import { queryClient } from '@/lib/queryClient';
import { uploadFile } from '@/lib/upload';
import { useCan } from '@/lib/permissions';
import { useUi } from '@/lib/store';
import { Avatar, Button, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Tabs, Textarea, ask } from '@/components/ui';
import { AudiencePicker } from '@/features/people/AudiencePicker';
import { TeamChip } from './TeamPicker';
import { PersonButton } from '@/features/people/PersonDrawer';
import { RelativeTime } from '@/components/time/RelativeTime';
import { useMobileLayout } from '@/mobile/layout';
import { BackHeader } from '@/mobile/buildings/common';
import clsx from 'clsx';
import { KudosSheet } from '@/mobile/KudosSheet';
import { CommentsPreview } from '@/features/threads/CommentsPreview';

const REACTIONS = ['👏', '🎉', '❤️', '🙌', '😂'];
const feedKey = (ws: string, filter: string) => ['kudos', ws, filter] as const;

/** Kudos / appreciation feed (backlog #5). Visible to the whole workspace. */
export default function KudosPage() {
  const nav = useNavigate();
  const openPerson = useUi(st => st.openPerson);
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'all' | 'mine'>('all');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());
  // ?give=<userId> (from a person's profile) opens the dialog with them picked.
  const [giving, setGiving] = useState(() => params.has('give'));
  const giveTo = params.get('give') || undefined;
  const canPost = useCan('kudos.post');
  // Phone layout (mobile Phase 2): the same flow as a bottom sheet.
  const phone = useMobileLayout();
  const Give = phone ? KudosSheet : GiveKudosModal;
  const feed = useInfiniteQuery({
    queryKey: [...feedKey(ws ?? '', tab), q],
    queryFn: ({ pageParam }) => api.kudosFeed(ws!, { before: pageParam ?? undefined, user_id: tab === 'mine' ? userId ?? undefined : undefined, q: q || undefined }),
    initialPageParam: null as string | null,
    // Kudos has no realtime topic: refresh reaction counts (and so who-reacted) every 30s while open.
    refetchInterval: 30_000,
    getNextPageParam: last => last.next_before,
    enabled: Boolean(ws),
  });
  const posts = useMemo(() => (feed.data?.pages ?? []).flatMap(p => p.kudos), [feed.data]);
  const highlight = params.get('post');
  useEffect(() => {
    if (highlight && posts.some(p => p.id === highlight)) document.getElementById(`kudos-${highlight}`)?.scrollIntoView({ block: 'center' });
  }, [highlight, posts]);

  const closeGive = (posted?: boolean) => {
    setGiving(false);
    // Nested-window rule: opened from someone's profile panel, cancelling goes back to that panel (and the page under it).
    if (giveTo && !posted && params.get('from') === 'person') {
      // The drawer closes itself on Back, so reopen it once the Back has landed.
      if (window.history.state?.idx > 0) { window.addEventListener('popstate', () => setTimeout(() => openPerson(giveTo), 0), { once: true }); nav(-1); }
      else { setParams(p => { p.delete('give'); p.delete('from'); return p; }, { replace: true }); openPerson(giveTo); }
      return;
    }
    if (giveTo) setParams(p => { p.delete('give'); p.delete('from'); return p; }, { replace: true });
  };
  // t344u #3: the app's lean phone Kudos - Back + title with a Give (+) button, full-width tabs and search, flat posts.
  if (phone) return (
    <div className="flex h-full flex-col bg-white dark:bg-slate-950" data-testid="m-kudos">
      <BackHeader fallback="/" title="Kudos" right={canPost ? <button type="button" onClick={() => setGiving(true)} aria-label="Give kudos" data-testid="kudos-give"
        className="flex h-11 w-11 items-center justify-center rounded-full active:bg-slate-100 dark:active:bg-slate-800"><Plus className="h-6 w-6" /></button> : undefined} />
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-2 p-3">
          <div role="tablist" className="flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
            {([['all', 'Everyone'], ['mine', 'For me & my departments']] as const).map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                className={clsx('h-10 flex-1 rounded-lg text-sm font-medium', tab === id ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white' : 'text-slate-600 dark:text-slate-300')}>{label}</button>
            ))}
          </div>
          <label className="flex min-h-[44px] items-center gap-2 rounded-xl bg-slate-100 px-3 dark:bg-slate-800">
            <Search className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
            <input type="search" placeholder="Search kudos, people or departments" aria-label="Search kudos" value={search} onChange={e => setSearch(e.target.value)} data-testid="kudos-search"
              className="min-h-[40px] min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-slate-500" />
          </label>
        </div>
        {feed.isLoading ? <div className="flex justify-center p-6"><Spinner /></div> : !posts.length ? (
          <p className="px-3 py-8 text-center text-sm text-slate-500">{q ? `No kudos match “${q}”` : 'No kudos yet. Thank someone for something they did this week.'}</p>
        ) : <div className="divide-y divide-slate-200 border-t border-slate-200 dark:divide-slate-800 dark:border-slate-800">{posts.map(p => <KudosCard key={p.id} flat post={p} highlighted={p.id === highlight} onDismissHighlight={() => setParams({})} />)}</div>}
        {feed.hasNextPage && <div className="p-3 text-center"><Button variant="secondary" loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>Load more</Button></div>}
      </div>
      {giving && <Give to={giveTo} onClose={closeGive} />}
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader icon={<Award className="h-5 w-5" />} title="Kudos"
        actions={canPost && <Button onClick={() => setGiving(true)}><Award className="h-4 w-4" />Give kudos</Button>} />
      <div className="scroll-thin flex-1 overflow-y-auto p-6">
        <div className="mx-auto w-full max-w-5xl space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Tabs tabs={[{ id: 'all', label: 'Everyone' }, { id: 'mine', label: 'For me & my departments' }]} value={tab} onChange={setTab} />
            <label className="relative block w-72 max-w-full">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" aria-hidden />
              <Input type="search" className="pl-8" placeholder="Search kudos, people or departments" aria-label="Search kudos" value={search} onChange={e => setSearch(e.target.value)} data-testid="kudos-search" />
            </label>
          </div>
          {feed.isLoading ? <Spinner /> : !posts.length && q ? (
            <EmptyState icon={<Search className="h-8 w-8" />} title={`No kudos match “${q}”`}>Try a name, a team or a word from the post.</EmptyState>
          ) : !posts.length ? (
            <EmptyState icon={<Award className="h-8 w-8" />} title="No kudos yet" action={<Button onClick={() => setGiving(true)}>Be the first</Button>}>Thank someone for something they did this week.</EmptyState>
          ) : posts.map(p => <KudosCard key={p.id} post={p} highlighted={p.id === highlight} onDismissHighlight={() => setParams({})} />)}
          {feed.hasNextPage && <div className="text-center"><Button variant="secondary" loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>Load more</Button></div>}
        </div>
      </div>
      {giving && <Give to={giveTo} onClose={closeGive} />}
    </div>
  );
}

const refreshFeed = (ws: string | null) => Promise.all([queryClient.invalidateQueries({ queryKey: ['kudos', ws ?? ''] }), queryClient.invalidateQueries({ queryKey: ['home', ws, 'feed'] })]);

export function KudosCard({ post, highlighted = false, onDismissHighlight, pinnedComposer, onComment, flat }: { post: KudosPost; highlighted?: boolean; onDismissHighlight?: () => void;
  /** t344u: the phone's flat full-width post (no card), badge in the meta line, no empty reactions. */ flat?: boolean;
  /** t202u: the sheet pins the comment box; Comment picks this post for it. */ pinnedComposer?: boolean; onComment?: (id: string) => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const moderate = useCan('kudos.moderate');
  const { byId } = useMembers();
  const toast = useUi(s => s.toast);
  const [showComments, setShowComments] = useState(false);
  // t247u: the API names the giver too, so a giver outside the loaded member list still shows.
  const member = post.author_id ? byId.get(post.author_id) : null;
  const author = post.author_id ? { display_name: member?.display_name ?? post.author_name ?? undefined, avatar_url: member?.avatar_url } : null;
  const badges = useKudosBadges();
  const badge = post.badge ? badges.byKey.get(post.badge) : null;
  const canDelete = post.author_id === userId || moderate;
  const toggle = async (emoji: string) => {
    const mine = post.reactions.find(r => r.emoji === emoji)?.user_ids.includes(userId ?? '');
    try { await (mine ? api.unreactKudos(post.id, emoji) : api.reactKudos(post.id, emoji)); void refreshFeed(ws); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const remove = async () => {
    if (!await ask('Delete this post?')) return;
    try { await api.deleteKudos(post.id); void refreshFeed(ws); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <article id={`kudos-${post.id}`} className={flat ? clsx('px-3 py-3', highlighted ? 'bg-slate-100 dark:bg-slate-800' : 'bg-white dark:bg-slate-950') : `rounded-xl border bg-white p-4 shadow-sm dark:bg-slate-900 ${highlighted ? 'border-accent-line ring-2 ring-accent' : 'border-slate-200 dark:border-slate-800'}`} onClick={highlighted ? onDismissHighlight : undefined}>
      <header className="flex items-start gap-3">
        {post.kind === 'birthday' ? <span className="grid h-9 w-9 place-items-center rounded-full bg-pink-100 dark:bg-pink-900/40"><Cake className="h-5 w-5 text-pink-600" /></span>
          : post.kind === 'sales_target' ? <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-yellow text-on-accent"><Target className="h-5 w-5" /></span>
          : <PersonButton userId={post.author_id} name={author?.display_name} className="rounded-full"><Avatar size={36} name={author?.display_name} id={post.author_id ?? undefined} url={author?.avatar_url} /></PersonButton>}
        <div className="min-w-0 flex-1">
          <p className="text-sm">
            {post.kind === 'birthday' || post.kind === 'sales_target' || !post.author_id
              ? <span className="font-semibold">{post.kind === 'birthday' ? 'Birthday' : post.kind === 'sales_target' ? 'Sales target' : author?.display_name ?? 'Former member'}</span>
              : <PersonButton userId={post.author_id} name={author?.display_name} className="rounded font-semibold hover:underline" testId="kudos-author">{author?.display_name ?? 'Former member'}</PersonButton>}
            <span className="text-slate-500"> {post.kind === 'birthday' ? 'for' : post.kind === 'sales_target' ? (post.recipients.length ? 'well done' : '') : 'recognised'} </span>
            {post.recipients.map((r, i) => <span key={r.user_id}>{i > 0 && ', '}<PersonButton userId={r.user_id} name={r.display_name} className="rounded font-medium hover:underline" testId="kudos-recipient">{r.display_name}</PersonButton></span>)}
            {post.recipients.length > 0 && post.teams.length > 0 && <span className="text-slate-500"> and </span>}
          </p>
          {post.teams.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{post.teams.map(t => <TeamChip key={t.team_id} team={{ id: t.team_id, name: t.name, kind: t.kind } as never} size="xs" />)}</div>}
          <p className="text-xs text-slate-500">{flat && badge && <span data-testid="kudos-badge">{badge.emoji} {badge.name} · </span>}<RelativeTime iso={post.created_at} /></p>
        </div>
        {badge && !flat && <BadgeChip badge={badge} />}
        {canDelete && post.kind === 'kudos' && <button aria-label="Delete post" className="text-slate-500 hover:text-rose-600" onClick={() => void remove()}><Trash2 className="h-4 w-4" /></button>}
      </header>
      <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed">{post.body}</p>
      {post.attachments.length > 0 && <div className="mt-3"><ImageGallery images={post.attachments} max={420} /></div>}
      <footer className="mt-3 flex flex-wrap items-center gap-1.5">
        <ReactionBar kind="kudos" id={post.id} size="md" showEmpty={!flat} onToggle={e => void toggle(e)}
          pills={[...REACTIONS, ...post.reactions.map(r => r.emoji).filter(e => !REACTIONS.includes(e))].map(e => post.reactions.find(x => x.emoji === e) ?? { emoji: e, count: 0, user_ids: [] })} />
        <button className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" onClick={() => { setShowComments(true); onComment?.(post.id); }} data-testid="kudos-comment">
          <MessageCircle className="h-4 w-4" />{post.comment_count ? `${post.comment_count} comment${post.comment_count > 1 ? 's' : ''}` : 'Comment'}
        </button>
      </footer>
      {/* t157u: comments are the post's discussion thread, with the full chat message actions. */}
      <CommentsPreview type="kudos" entityId={post.id} count={post.comment_count} open={showComments} hideButton hideComposer={pinnedComposer} />
    </article>
  );
}

export function GiveKudosModal({ onClose: done, to }: { onClose: (posted?: boolean) => void; to?: string }) {
  const onClose = () => done(false);
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const [people, setPeople] = useState<string[]>(to ? [to] : []);
  // t148: opened for one person (profile, ?give=): just their chip until "Add someone else" is clicked.
  const [expanded, setExpanded] = useState(!to);
  const { byId: memberById } = useMembers();
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const badgeDefs = useKudosBadges();
  const [picked, setBadge] = useState<string>('');
  const badge = picked || badgeDefs.pickable[0]?.key || '';
  const [body, setBody] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [photos, setPhotos] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const addPhoto = async (f: File) => {
    if (!ws) return;
    setBusy(true);
    try { const a = await uploadFile(f, { workspace_id: ws, purpose: 'kudos' }); setPhotos(p => [...p, { id: a.id, name: f.name }]); }
    catch (e) { toast(uploadFailureText(errorMessage(e), "Couldn't upload photo"), 'error'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      await api.createKudos(ws, { body: body.trim(), badge: badge || undefined, recipient_ids: people, team_ids: teamIds, attachment_ids: photos.map(p => p.id) });
      void refreshFeed(ws);
      toast('Kudos posted 🎉', 'success');
      done(true);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const valid = body.trim().length > 0 && (people.length > 0 || teamIds.length > 0);
  return (
    <Modal open onClose={onClose} title="Give kudos" wide
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!valid} loading={busy} onClick={() => void submit()}>Post kudos</Button></>}>
      <div className="space-y-4">
        {expanded ? <Field label="Who are you thanking?">{/* t683u: one picker for people, departments and groups, no suggested people */}
          <AudiencePicker value={{ people, teams: teamIds }} onChange={v => { setPeople(v.people.filter(id => id !== userId)); setTeamIds(v.teams); }} allow={(k, id) => k !== 'person' || id !== userId} label="Search people to thank"
            placeholder="Search people, departments and groups" helper={teamIds.length ? 'Everyone in a department or group you pick is credited.' : undefined} /></Field> : (
          <Field label="To">
            <div className="flex flex-wrap items-center gap-1.5" data-testid="kudos-to">
              {people.map(id => { const m = memberById.get(id); return (
                <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white py-0.5 pl-0.5 pr-1 text-sm dark:border-slate-600 dark:bg-slate-800" data-testid="kudos-to-chip">
                  <Avatar size={22} name={m?.display_name} id={id} />{m?.display_name ?? 'Someone'}
                  <button type="button" aria-label={`Remove ${m?.display_name ?? 'person'}`} onClick={() => { const next = people.filter(x => x !== id); setPeople(next); if (!next.length) setExpanded(true); }}
                    className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:text-white"><X className="h-3.5 w-3.5" /></button>
                </span>
              ); })}
              <Button size="sm" variant="ghost" onClick={() => { setExpanded(true); setTimeout(() => document.querySelector<HTMLInputElement>('[data-testid="people-picker-input"]')?.focus(), 0); }} data-testid="kudos-add-someone"><UserPlus className="h-4 w-4" />Add someone else</Button>
            </div>
          </Field>
        )}
        <Field label="Badge">
          <Select value={badge} onChange={e => setBadge(e.target.value)} data-testid="badge-select">
            {badgeDefs.pickable.map(b => <option key={b.key} value={b.key} title={b.description ?? undefined}>{b.emoji} {b.name}</option>)}
          </Select>
        </Field>
        <Field label="Message">
          <div className="relative">
            <Textarea ref={bodyRef} rows={4} value={body} onChange={e => setBody(e.target.value)} maxLength={5000} placeholder="What did they do that you appreciated?" className="pb-9" />
            <div className="absolute bottom-1.5 left-1.5"><EmojiButton onPick={e => { const r = insertAtCaret(bodyRef.current, body, e); setBody(r.value); restoreCaret(bodyRef.current, r.caret); }} /></div>
          </div>
        </Field>
        <Field label="Photos (optional)">
          <div className="flex flex-wrap items-center gap-2">
            {photos.map(p => <span key={p.id} className="rounded-md bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">{p.name}</span>)}
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy || photos.length >= 10}><ImagePlus className="h-4 w-4" />Add photo</Button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void addPhoto(f); e.target.value = ''; }} />
          </div>
        </Field>
      </div>
    </Modal>
  );
}
